import * as React from 'react';
import { apiClient } from '../../../../shared/utils/apiClient';
import type { JobApplicationRecord } from '../../../../shared/types/domainModels';

interface PaginationState {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

export interface Filters {
  nameFilter: string;
  dateFilter: string;
  statusFilter: string;
  locationFilter: string;
  positionTypeFilter: string;
  jobTypeFilter: string;
}

const DEFAULT_FILTERS: Filters = {
  nameFilter: '', dateFilter: '', statusFilter: '',
  locationFilter: '', positionTypeFilter: '', jobTypeFilter: ''
};

interface AllApplicantsState {
  applicants: JobApplicationRecord[];
  loading: boolean;
  error: string | null;
  statistics: Record<string, any>;
  filterOptions: Record<string, any>;
  pagination: PaginationState;
}

export function useAllApplicants() {
  const [state, setState] = React.useState<AllApplicantsState>({
    applicants: [],
    loading: true,
    error: null,
    statistics: {},
    filterOptions: {},
    pagination: { current_page: 1, total_pages: 1, total_count: 0, per_page: 25 },
  });

  const [page, setPage] = React.useState(1);
  const [tab, setTab] = React.useState('applicants');
  const [filters, setFilters] = React.useState<Filters>(DEFAULT_FILTERS);

  // Debounce text inputs (name, location) — only send API request after 300ms
  const [debouncedName, setDebouncedName] = React.useState('');
  const [debouncedLocation, setDebouncedLocation] = React.useState('');
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedName(filters.nameFilter), 300);
    return () => clearTimeout(timer);
  }, [filters.nameFilter]);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedLocation(filters.locationFilter), 300);
    return () => clearTimeout(timer);
  }, [filters.locationFilter]);

  const fetchApplicants = React.useCallback(async (
    fetchPage: number,
    fetchTab: string,
    fetchFilters: {
      search: string; location: string; dateFilter: string;
      statusFilter: string; positionType: string; jobType: string;
    }
  ) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const params = new URLSearchParams({
        page: String(fetchPage),
        per_page: '25',
        tab: fetchTab,
      });

      if (fetchFilters.search) params.set('search', fetchFilters.search);
      if (fetchFilters.location) params.set('location', fetchFilters.location);
      if (fetchFilters.dateFilter) params.set('date_filter', fetchFilters.dateFilter);
      if (fetchFilters.statusFilter) params.set('status', fetchFilters.statusFilter);
      if (fetchFilters.positionType) params.set('position_type', fetchFilters.positionType);
      if (fetchFilters.jobType) params.set('job_type', fetchFilters.jobType);

      const response = await apiClient.get<{
        job_applications?: JobApplicationRecord[];
        statistics?: Record<string, any>;
        filter_options?: Record<string, any>;
        pagination?: PaginationState;
      }>(`/api/v1/employer/job_applications?${params}`);

      if (!response.ok) {
        throw new Error(response.error || 'Failed to fetch applicants');
      }
      const data = response.data;

      setState(prev => ({
        ...prev,
        applicants: data?.job_applications || [],
        statistics: data?.statistics || {},
        filterOptions: data?.filter_options || {},
        pagination: data?.pagination || prev.pagination,
        loading: false
      }));
    } catch (error: any) {
      console.error('Error fetching applicants:', error);
      setState(prev => ({
        ...prev,
        error: error.message,
        loading: false
      }));
    }
  }, []);

  const buildFetchFilters = React.useCallback(() => ({
    search: debouncedName,
    location: debouncedLocation,
    dateFilter: filters.dateFilter,
    statusFilter: filters.statusFilter,
    positionType: filters.positionTypeFilter,
    jobType: filters.jobTypeFilter,
  }), [debouncedName, debouncedLocation, filters.dateFilter, filters.statusFilter, filters.positionTypeFilter, filters.jobTypeFilter]);

  // Fetch when page, tab, or filters change
  React.useEffect(() => {
    fetchApplicants(page, tab, buildFetchFilters());
  }, [fetchApplicants, page, tab, buildFetchFilters]);

  const navigateTo = React.useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const switchTab = React.useCallback((newTab: string) => {
    setTab(newTab);
    setPage(1);
  }, []);

  const updateFilters = React.useCallback((newFilters: Filters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  // Optimistic in-place update. Status changes must NOT be reconciled with a
  // blind delayed refetch: the refetch races the update request, and when its
  // read beats the write the stale payload flows down and reverts the UI.
  const patchApplication = React.useCallback((id: number, attrs: Partial<JobApplicationRecord>) => {
    setState(prev => ({
      ...prev,
      applicants: prev.applicants.map(a => (a.id === id ? { ...a, ...attrs } : a)),
    }));
  }, []);

  return {
    ...state,
    page,
    tab,
    filters,
    navigateTo,
    switchTab,
    updateFilters,
    patchApplication,
    refetch: () => fetchApplicants(page, tab, buildFetchFilters()),
  };
}
